import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Package, Truck, Home, Sparkles } from "lucide-react";
import { api, inr } from "../lib/api";

const STEPS = [
  { id: "confirmed", label: "Confirmed", icon: Check },
  { id: "packed", label: "Packed", icon: Package },
  { id: "shipped", label: "Shipped", icon: Truck },
  { id: "delivered", label: "Delivered", icon: Home },
];

const OrderConfirmation = () => {
  const [params] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [missing, setMissing] = useState(false);
  const orderNumber = params.get("order");

  useEffect(() => {
    if (!orderNumber) { setMissing(true); return; }
    api.get(`/orders/${orderNumber}`).then((r) => setOrder(r.data)).catch(() => setMissing(true));
  }, [orderNumber]);

  if (missing) {
    return (
      <main className="pt-[68px] min-h-screen flex items-center justify-center px-6" data-testid="confirmation-missing">
        <div className="text-center">
          <p className="font-serif text-4xl text-zinc-200">Order not found</p>
          <Link to="/shop" className="btn-gold mt-6">Back to Shop</Link>
        </div>
      </main>
    );
  }

  if (!order) {
    return (
      <main className="pt-[68px] min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 rounded-full border-2 border-[#d4af37]/30 border-t-[#d4af37] animate-spin" />
      </main>
    );
  }

  const statusIdx = { confirmed: 0, shipped: 2, delivered: 3 }[order.order_status] ?? 0;

  return (
    <main className="pt-[68px] min-h-screen" data-testid="confirmation-page">
      <div className="max-w-3xl mx-auto px-5 md:px-8 py-16 md:py-24">
        <motion.div
          initial={{ scale: 0, rotate: -30 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: "spring", stiffness: 200, damping: 14, delay: 0.1 }}
          className="w-20 h-20 mx-auto rounded-full bg-[#d4af37]/10 border border-[#d4af37]/40 flex items-center justify-center"
          data-testid="confirmation-badge"
        >
          <Check size={34} className="text-[#f3e5ab]" strokeWidth={2} />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="text-center mt-8"
        >
          <p className="eyebrow mb-3">Payment {order.payment_status === "paid" ? "Received" : "Pending"}</p>
          <h1 className="font-serif font-light text-4xl md:text-5xl text-zinc-100">
            Thank you, <span className="italic text-gold-gradient">{order.customer.name.split(" ")[0]}</span>
          </h1>
          <p className="mt-4 text-zinc-500 text-sm">
            Order <span className="text-[#f3e5ab] font-medium" data-testid="confirmation-order-number">{order.order_number}</span>
            {" "}is confirmed. A sparkle is being prepared for {order.address.city}.
          </p>
        </motion.div>

        <div className="mt-14 rounded-2xl border border-[#d4af37]/25 bg-[#101013] p-6 md:p-8" data-testid="confirmation-timeline">
          <div className="flex items-center justify-between">
            {STEPS.map((s, i) => (
              <div key={s.id} className="flex flex-col items-center relative flex-1">
                {i < STEPS.length - 1 && (
                  <div className={`absolute top-4 left-1/2 w-full h-px ${i < statusIdx ? "bg-[#d4af37]" : "bg-[#d4af37]/20"}`} />
                )}
                <span
                  className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center border ${
                    i <= statusIdx ? "bg-[#d4af37] text-[#0a0a0b] border-[#d4af37]" : "bg-[#0c0c0e] text-zinc-600 border-[#d4af37]/25"
                  }`}
                >
                  <s.icon size={14} strokeWidth={2} />
                </span>
                <span className={`mt-2 text-[10px] tracking-[0.14em] uppercase ${i <= statusIdx ? "text-[#f3e5ab]" : "text-zinc-600"}`}>
                  {s.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 rounded-2xl border border-[#d4af37]/25 bg-[#101013] p-6 md:p-8" data-testid="confirmation-items">
          <h2 className="font-serif text-2xl text-zinc-100 mb-6">Your Pieces</h2>
          <div className="space-y-4">
            {order.items.map((i) => (
              <div key={i.product_id} className="flex gap-4 items-center" data-testid={`confirmation-item-${i.product_id}`}>
                <img src={i.image} alt={i.name} className="w-16 h-20 object-cover rounded-lg border border-[#d4af37]/15" />
                <div className="flex-1">
                  <p className="font-serif text-lg text-zinc-100">{i.name}</p>
                  <p className="text-xs text-zinc-500 mt-0.5">Qty {i.qty}</p>
                </div>
                <p className="text-sm text-zinc-300">{inr(i.price * i.qty)}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 pt-5 border-t border-[#d4af37]/15 space-y-2 text-sm">
            <div className="flex justify-between text-zinc-400"><span>Subtotal</span><span>{inr(order.subtotal)}</span></div>
            {order.discount > 0 && <div className="flex justify-between text-emerald-400"><span>Discount</span><span>−{inr(order.discount)}</span></div>}
            <div className="flex justify-between text-zinc-400"><span>Shipping</span><span>{order.shipping === 0 ? "Free" : inr(order.shipping)}</span></div>
            <div className="flex justify-between text-zinc-100 font-semibold pt-2 border-t border-[#d4af37]/15" data-testid="confirmation-total">
              <span>Total Paid</span><span>{inr(order.total)}</span>
            </div>
          </div>
        </div>

        <div className="mt-8 rounded-xl border border-[#d4af37]/15 bg-[#0c0c0e] p-5 flex gap-3 items-start" data-testid="confirmation-care-note">
          <Sparkles size={16} className="text-[#d4af37] mt-0.5 shrink-0" />
          <p className="text-xs text-zinc-500 leading-relaxed">
            Each piece ships in an Jwellary pouch with its care card. Keep away from water and
            perfume to keep the gold luminous.
          </p>
        </div>

        <div className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
          <Link to="/shop" className="btn-gold" data-testid="confirmation-continue-button">Continue Shopping</Link>
          <Link to="/track" state={{ order_number: order.order_number, email: order.customer.email }} className="btn-ghost" data-testid="confirmation-track-button">
            Track This Order
          </Link>
        </div>
      </div>
    </main>
  );
};

export default OrderConfirmation;
